import { useEffect, useState } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useEntitlements } from '@/hooks/use-entitlements';
import { customersQueryKey, listCustomers } from '@/lib/api/customers';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';

import {
  addMinutesToTime,
  addYears,
  availablePaymentMethodOptions,
  formatTimeOption,
  jobToAddJobInput,
  paymentMethodOptionValue,
  TIME_OPTIONS,
  toJobRecurrence,
} from './helpers';
import { RecurrenceFields } from './recurrence-fields';
import { RecurrencePreview } from './recurrence-preview';
import { addJobSchema, type AddJobInput, type RecurrenceFormValues } from './schema';
import type { Job, NewJobSubmission } from './types';

interface Props {
  businessId: string;
  onSave: (submission: NewJobSubmission) => void;
  onClose: () => void;
  isSaving: boolean;
  /** When set, the form is prefilled from this job (the "Copy Job" action). */
  sourceJob?: Job;
}

const NEW_CUSTOMER_VALUE = '__new__';

type EndDateMode = 'same' | 'custom' | 'year';

const DEFAULT_VALUES: AddJobInput = {
  customerMode: 'existing',
  customerUserId: '',
  newCustomerName: '',
  newCustomerEmail: '',
  newCustomerPhone: '',
  title: '',
  description: '',
  serviceAddress: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  totalAmount: '',
  defaultPaymentMethod: '',
  repeats: false,
  recurrence: {
    frequency: 'weekly',
    intervalCount: '1',
    daysOfWeek: [],
    dayOfMonth: '',
    customRule: '',
    seriesEndDate: '',
  },
};

export function AddJobModal({ businessId, onSave, onClose, isSaving, sourceJob }: Props) {
  const { t } = useLanguage();
  const { has } = useEntitlements();
  const canRecur = has('recurring_jobs');

  const form = useForm<AddJobInput>({
    defaultValues: sourceJob ? jobToAddJobInput(sourceJob) : DEFAULT_VALUES,
    resolver: zodResolver(addJobSchema),
  });

  const customersQ = useQuery({
    queryKey: customersQueryKey(businessId, 'active'),
    queryFn: () => listCustomers(businessId, 'active'),
  });

  const paymentMethodsQ = useQuery({
    queryKey: paymentMethodsQueryKey(businessId),
    queryFn: () => listPaymentMethods(businessId),
  });

  const eligibleCustomers = (customersQ.data ?? []).filter((c) => c.customer_user_id);

  const customerMode = form.watch('customerMode');
  const { errors } = form.formState;

  const [endDateMode, setEndDateMode] = useState<EndDateMode>(() => {
    if (!sourceJob) return 'custom';
    return sourceJob.end_date && sourceJob.end_date !== sourceJob.start_date ? 'custom' : 'same';
  });
  const startDate = form.watch('startDate');
  const startTime = form.watch('startTime');
  const repeats = form.watch('repeats');

  useEffect(() => {
    if (endDateMode === 'same') {
      form.setValue('endDate', startDate);
    } else if (endDateMode === 'year') {
      form.setValue('endDate', addYears(startDate, 1));
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [endDateMode, startDate]);

  useEffect(() => {
    if (!startTime) return;
    const endTime = form.getValues('endTime');
    if (!endTime || endTime <= startTime) {
      form.setValue('endTime', addMinutesToTime(startTime, 30));
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [startTime]);

  useEffect(() => {
    if (repeats && !form.getValues('recurrence.seriesEndDate')) {
      form.setValue('recurrence.seriesEndDate', form.getValues('endDate') || '');
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [repeats]);

  // "Copy Job" prefills the field from the source job's bare enum (see
  // `jobToAddJobInput`) before the payment-methods list has loaded; once it
  // has, re-resolve to the option the configured account actually uses (if
  // the user hasn't already touched the field).
  useEffect(() => {
    if (!sourceJob || !paymentMethodsQ.data) return;
    if (form.formState.dirtyFields.defaultPaymentMethod) return;
    const options = availablePaymentMethodOptions(t, paymentMethodsQ.data, has('stripe_payments'));
    form.setValue(
      'defaultPaymentMethod',
      paymentMethodOptionValue(
        options,
        sourceJob.default_payment_method,
        sourceJob.default_payment_method_id,
      ),
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [sourceJob, paymentMethodsQ.data]);

  function handleSelectChange(value: string) {
    if (value === NEW_CUSTOMER_VALUE) {
      form.setValue('customerMode', 'new');
      form.setValue('customerUserId', '');
    } else {
      form.setValue('customerMode', 'existing');
      form.setValue('customerUserId', value);
    }
  }

  function handleSubmit(data: AddJobInput) {
    const selectedPaymentMethod = availablePaymentMethodOptions(
      t,
      paymentMethodsQ.data ?? [],
      has('stripe_payments'),
    ).find((o) => o.value === data.defaultPaymentMethod);

    onSave({
      mode: data.customerMode,
      customerUserId:
        data.customerMode === 'existing' ? data.customerUserId : undefined,
      newCustomerName:
        data.customerMode === 'new' ? data.newCustomerName : undefined,
      newCustomerEmail:
        data.customerMode === 'new' ? data.newCustomerEmail : undefined,
      newCustomerPhone:
        data.customerMode === 'new' ? data.newCustomerPhone : undefined,
      job: {
        title: data.title,
        description: data.description || undefined,
        service_address: data.serviceAddress || undefined,
        start_date: data.startDate,
        end_date: data.endDate || undefined,
        start_time: data.startTime || undefined,
        end_time: data.endTime || undefined,
        total_amount: data.totalAmount ? Number(data.totalAmount) : undefined,
        default_payment_method: selectedPaymentMethod?.type ?? undefined,
        default_payment_method_id: selectedPaymentMethod?.id ?? null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
        recurrence:
          canRecur && data.repeats && data.recurrence
            ? toJobRecurrence(data.recurrence)
            : undefined,
      },
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">
            {sourceJob ? t.copyJobTitle : t.addJobTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={form.handleSubmit(handleSubmit)} className="overflow-y-auto flex-1">
          <div className="px-6 py-5 flex flex-col gap-4">
            {/* Customer */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="job-customer">{t.jobCustomer}</Label>
              <select
                id="job-customer"
                data-testid="job-customer-select"
                value={
                  customerMode === 'new'
                    ? NEW_CUSTOMER_VALUE
                    : (form.watch('customerUserId') ?? '')
                }
                onChange={(e) => handleSelectChange(e.target.value)}
                aria-invalid={Boolean(errors.customerUserId)}
                className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="" disabled>
                  {t.jobCustomerPlaceholder}
                </option>
                {eligibleCustomers.map((c) => (
                  <option key={c.id} value={c.customer_user_id ?? ''}>
                    {c.full_name}
                  </option>
                ))}
                <option value={NEW_CUSTOMER_VALUE}>{t.jobCustomerNew}</option>
              </select>
              {!customersQ.isPending && eligibleCustomers.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t.jobNoEligibleCustomers}
                </p>
              )}
              {errors.customerUserId && (
                <p className="text-xs text-destructive">
                  {errors.customerUserId.message}
                </p>
              )}
            </div>

            {customerMode === 'new' && (
              <>
                <Controller
                  control={form.control}
                  name="newCustomerName"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="job-new-customer">{t.newCustomerName}</Label>
                      <Input
                        {...field}
                        id="job-new-customer"
                        placeholder={t.newCustomerNamePlaceholder}
                        aria-invalid={fieldState.invalid}
                        data-testid="new-customer-name-input"
                      />
                      {fieldState.error && (
                        <p className="text-xs text-destructive">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="newCustomerEmail"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="job-new-customer-email">
                        {t.customerEmail}
                      </Label>
                      <Input
                        {...field}
                        id="job-new-customer-email"
                        type="email"
                        placeholder={t.customerEmailPlaceholder}
                        aria-invalid={fieldState.invalid}
                        data-testid="new-customer-email-input"
                      />
                      {fieldState.error && (
                        <p className="text-xs text-destructive">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  control={form.control}
                  name="newCustomerPhone"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="job-new-customer-phone">
                        {t.employeePhone}
                      </Label>
                      <Input
                        {...field}
                        id="job-new-customer-phone"
                        type="tel"
                        placeholder={t.phonePlaceholder}
                        aria-invalid={fieldState.invalid}
                        data-testid="new-customer-phone-input"
                      />
                      {fieldState.error && (
                        <p className="text-xs text-destructive">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </>
            )}

            {/* Title */}
            <Controller
              control={form.control}
              name="title"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-title">{t.jobTitle}</Label>
                  <Input
                    {...field}
                    id="job-title"
                    placeholder={t.jobTitlePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="job-title-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            {/* Description */}
            <Controller
              control={form.control}
              name="description"
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-description">{t.jobDescription}</Label>
                  <textarea
                    {...field}
                    id="job-description"
                    placeholder={t.jobDescriptionPlaceholder}
                    rows={2}
                    data-testid="job-description-input"
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                </div>
              )}
            />

            {/* Service address */}
            <Controller
              control={form.control}
              name="serviceAddress"
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-address">{t.jobAddress}</Label>
                  <Input
                    {...field}
                    id="job-address"
                    data-testid="job-address-input"
                  />
                </div>
              )}
            />

            {/* Dates + times */}
            <div className="grid grid-cols-2 gap-3">
              <Controller
                control={form.control}
                name="startDate"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="job-start-date">{t.jobStartDate}</Label>
                    <Input
                      {...field}
                      id="job-start-date"
                      type="date"
                      aria-invalid={fieldState.invalid}
                      data-testid="job-start-date-input"
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                control={form.control}
                name="endDate"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="job-end-date">{t.jobEndDate}</Label>
                    <ViewToggle
                      value={endDateMode}
                      onChange={setEndDateMode}
                      aria-label={t.jobEndDate}
                      options={[
                        { value: 'same', label: t.jobEndDateModeSame, testId: 'job-end-date-mode-same' },
                        { value: 'custom', label: t.jobEndDateModeCustom, testId: 'job-end-date-mode-custom' },
                        // A year-out end date implies a recurring engagement,
                        // which this tier can't actually schedule.
                        ...(canRecur
                          ? [{ value: 'year' as const, label: t.jobEndDateModeYear, testId: 'job-end-date-mode-year' }]
                          : []),
                      ]}
                    />
                    {endDateMode === 'custom' && (
                      <Input
                        {...field}
                        id="job-end-date"
                        type="date"
                        aria-invalid={fieldState.invalid}
                        data-testid="job-end-date-input"
                      />
                    )}
                    {fieldState.error && (
                      <p className="text-xs text-destructive">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                control={form.control}
                name="startTime"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="job-start-time">{t.jobStartTime}</Label>
                    <select
                      {...field}
                      id="job-start-time"
                      data-testid="job-start-time-input"
                      aria-invalid={fieldState.invalid}
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">{t.jobSelectTimePlaceholder}</option>
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>
                          {formatTimeOption(time)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              />
              <Controller
                control={form.control}
                name="endTime"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="job-end-time">{t.jobEndTime}</Label>
                    <select
                      {...field}
                      id="job-end-time"
                      data-testid="job-end-time-input"
                      aria-invalid={fieldState.invalid}
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">{t.jobSelectTimePlaceholder}</option>
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>
                          {formatTimeOption(time)}
                        </option>
                      ))}
                    </select>
                    {fieldState.error && (
                      <p className="text-xs text-destructive">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Total amount */}
            <Controller
              control={form.control}
              name="totalAmount"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-total">{t.jobTotalAmount}</Label>
                  <Input
                    {...field}
                    id="job-total"
                    inputMode="decimal"
                    placeholder={t.jobTotalAmountPlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="job-total-input"
                    className="w-40"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            {/* Default payment method */}
            <Controller
              control={form.control}
              name="defaultPaymentMethod"
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="job-default-payment-method">
                    {t.jobDefaultPaymentMethod}
                  </Label>
                  <select
                    {...field}
                    id="job-default-payment-method"
                    data-testid="job-default-payment-method"
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">{t.jobDefaultPaymentMethodNone}</option>
                    {availablePaymentMethodOptions(
                      t,
                      paymentMethodsQ.data ?? [],
                      has('stripe_payments'),
                    ).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            />

            {/* Both job forms embed `recurrenceFormFields`; RHF Control is invariant. */}
            {canRecur && (
              <>
                <RecurrenceFields
                  control={
                    form.control as unknown as Control<RecurrenceFormValues>
                  }
                />
                <RecurrencePreview
                  control={
                    form.control as unknown as Control<RecurrenceFormValues>
                  }
                  startDate={form.watch('startDate')}
                />
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button type="submit" size="sm" disabled={isSaving} data-testid="save-job">
              {t.saveJob}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
